import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Handshake, Plus, Edit, Trash2, Globe, Mail, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import RichTextEditor from '../../components/ui/rich-text-editor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTNER_TYPES = ['Ministry', 'Business', 'Nonprofit', 'Church', 'Community Organization'];

export default function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    logo: '',
    partner_type: 'Ministry',
    contact_person: '',
    contact_email: '',
    is_visible: true
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/partners`);
      setPartners(response.data);
    } catch (error) {
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPartner) {
        await axios.put(`${API}/partners/${editingPartner.id}`, formData);
        toast.success('Partner updated');
      } else {
        await axios.post(`${API}/partners`, formData);
        toast.success('Partner created');
      }
      setShowDialog(false);
      resetForm();
      fetchPartners();
    } catch (error) {
      toast.error('Failed to save partner');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this partner?')) return;
    try {
      await axios.delete(`${API}/partners/${id}`);
      toast.success('Partner deleted');
      fetchPartners();
    } catch (error) {
      toast.error('Failed to delete partner');
    }
  };

  const openEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name || '',
      description: partner.description || '',
      website: partner.website || '',
      logo: partner.logo || '',
      partner_type: partner.partner_type || 'Ministry',
      contact_person: partner.contact_person || '',
      contact_email: partner.contact_email || '',
      is_visible: partner.is_visible !== false
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingPartner(null);
    setFormData({
      name: '',
      description: '',
      website: '',
      logo: '',
      partner_type: 'Ministry',
      contact_person: '',
      contact_email: '',
      is_visible: true
    });
  };

  const getTypeColor = (type) => {
    const colors = {
      'Ministry': 'bg-purple/10 text-purple',
      'Business': 'bg-blue-100 text-blue-600',
      'Nonprofit': 'bg-olive/10 text-olive',
      'Church': 'bg-burgundy/10 text-burgundy',
      'Community Organization': 'bg-gold/10 text-gold'
    };
    return colors[type] || 'bg-gray-100 text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-partners-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-serif text-charcoal">Partners</h1>
          <p className="text-charcoal/60">Manage partner organizations</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowDialog(true); }}
          className="bg-burgundy hover:bg-purple text-white"
          data-testid="add-partner-btn"
        >
          <Plus size={18} className="mr-2" /> Add Partner
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {partners.map((partner) => (
          <motion.div
            key={partner.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 border border-gold/10 hover:border-gold/30 transition-all"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-4">
                {partner.logo ? (
                  <img src={partner.logo} alt={partner.name} className="w-16 h-16 object-contain rounded-xl bg-cream p-2" />
                ) : (
                  <div className="w-16 h-16 bg-gold/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Handshake size={28} className="text-gold" />
                  </div>
                )}
                <div>
                  <h3 className="font-serif text-lg text-charcoal">{partner.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getTypeColor(partner.partner_type)}`}>
                      {partner.partner_type}
                    </span>
                    {!partner.is_visible && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600">Hidden</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEdit(partner)} className="p-2 text-charcoal/50 hover:text-burgundy">
                  <Edit size={18} />
                </button>
                <button onClick={() => handleDelete(partner.id)} className="p-2 text-charcoal/50 hover:text-red-500">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            
            <div className="text-sm text-charcoal/70 mb-3 line-clamp-2" 
                 dangerouslySetInnerHTML={{ __html: partner.description }} />
            
            <div className="flex flex-wrap gap-3 text-sm text-charcoal/50">
              {partner.website && (
                <a href={partner.website} target="_blank" rel="noopener noreferrer" 
                   className="flex items-center gap-1 text-gold hover:underline">
                  <Globe size={14} /> Website
                </a>
              )}
              {partner.contact_person && (
                <span className="flex items-center gap-1">
                  <User size={14} /> {partner.contact_person}
                </span>
              )}
              {partner.contact_email && (
                <span className="flex items-center gap-1">
                  <Mail size={14} /> {partner.contact_email}
                </span>
              )}
            </div>
          </motion.div>
        ))}
        {partners.length === 0 && (
          <div className="col-span-2 text-center py-12 text-charcoal/50">
            <Handshake size={48} className="mx-auto mb-4 opacity-20" />
            <p>No partners yet. Add your first partner!</p>
          </div>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editingPartner ? 'Edit Partner' : 'Add Partner'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Organization Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Partner Type</Label>
                <Select value={formData.partner_type} onValueChange={(v) => setFormData({...formData, partner_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PARTNER_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Website URL</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Person</Label>
                <Input
                  value={formData.contact_person}
                  onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                />
              </div>
              <div>
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Logo URL</Label>
              <Input
                value={formData.logo}
                onChange={(e) => setFormData({...formData, logo: e.target.value})}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label>Description *</Label>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => setFormData({...formData, description: value})}
                placeholder="Partner description..."
                height="150px"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_visible"
                checked={formData.is_visible}
                onChange={(e) => setFormData({...formData, is_visible: e.target.checked})}
              />
              <Label htmlFor="is_visible">Visible on website</Label>
            </div>
            <Button type="submit" className="w-full bg-burgundy hover:bg-purple">
              {editingPartner ? 'Update Partner' : 'Create Partner'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
